import { Breadcrumb } from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import { Input } from '@/components/ui/input'
import { Progress } from '@/components/ui/progress'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableErrorRow,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import {
  useDataQualScoreCardsQuery,
  useMetaDataScoreCardsQuery,
} from '@/hooks/api/use-datagovernance-dashboard-query'
import { TagsStructure } from '@/hooks/api/use-table-details-query'
import { TablesQueryParams, useTablesQuery } from '@/hooks/api/use-table-query'
import {
  CellContext,
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { T } from '@tolgee/react'
import { SearchIcon, Target, TrendingUp } from 'lucide-react'
import React from 'react'
import { PageTemplate } from '../_common/page-template'
import {
  EditableFieldName,
  EnrichedTableType,
  EnrichmentControls,
  EnrichmentControlsState,
  NameButton,
  OwnersCell,
  useEnrichmentHandlers,
  usePersistentTableSelection,
  useTableEnrichment,
} from './_common'
import { EditableCell } from './_common/components/editable-cell'
import { SensitivityTagsCell, TagsCell } from './_common/components/tables-common-cells'
import { TAG_CATEGORIES } from './_common/constants'
import {
  useDataCatalogNavigation,
  useTablePageBreadcrumbs,
} from './_common/hooks/use-data-catalog-breadcrumbs'
import { useSaveMetadata } from './_common/hooks/use-save-metadata'
import { useEditing } from './_common/hooks/use-table-cell-editing'
import {
  extractDataQualityTags,
  extractPiiTags,
  extractSensitivityTags,
  getEnrichmentFields,
  shouldDisableClearAllButtonForTables,
} from './_common/utils'
import { ScoreCardsUpdate } from './dashboard/_components/_common/scorecards-update'
import { getStyles } from './dashboard/utils'

type TableListProps = Readonly<TablesQueryParams>

function TableList(props: TableListProps) {
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState('')
  const { openMetadataTab, openDataGovernanceDashboardTab } = useDataCatalogNavigation()
  const { breadcrumbItems } = useTablePageBreadcrumbs(
    props.database_name,
    props.schema_name,
    props.assetType,
  )

  const [enrichmentState, setEnrichmentState] = React.useState<EnrichmentControlsState>({
    selectedAgent: 'emAgents',
    showEnrichmentControls: false,
  })

  const tableQuery = useTablesQuery(props)

  const metadataScoreQuery = useMetaDataScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: props.schema_name,
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  const dataQualityScore = useDataQualScoreCardsQuery({
    filters: {
      database: props.database_name,
      schema: props.schema_name,
      table: 'all',
      sensitivity: 'all',
    },
    queryOptions: {
      enabled: true,
    },
  })

  // Function to refetch score queries
  const refetchScoreQueries = React.useCallback(() => {
    metadataScoreQuery.refetch()
    dataQualityScore.refetch()
  }, [metadataScoreQuery, dataQualityScore])

  const isNoData = tableQuery.isError || tableQuery.isPending || tableQuery.data.tables.length === 0

  const enrichmentWorkflow = useTableEnrichment(props.schema_name, tableQuery.data)

  const tables = tableQuery.data?.tables ?? []
  const memoizedTables = React.useMemo(() => tables, [tableQuery.data?.tables])

  const tableSelection = usePersistentTableSelection({
    items: memoizedTables,
    getId: (table) => table.table_name,
    onSelectionChange: (selectedIds) => {
      enrichmentWorkflow.updateSelectedTableIds(selectedIds)
    },
    schemaName: props.schema_name,
  })

  const selectedTableIds = tableSelection.selectedIds

  const handlers = useEnrichmentHandlers(
    'table',
    enrichmentWorkflow,
    tableQuery,
    setEnrichmentState,
    props,
    enrichmentState,
    undefined,
    refetchScoreQueries,
  )

  const handleNavigate = (tableName: string) => {
    openMetadataTab(props.database_name, props.schema_name, tableName, props.assetType)
  }

  const enrichmentFields = getEnrichmentFields(enrichmentWorkflow.state.selectedMetadata || [])

  const { editingState, editingHandlers, editingRefs } = useEditing([], memoizedTables as any[])

  const saveMetadata = useSaveMetadata({
    queryParams: props,
    context: 'editing',
    refetchScoreQueries,
  })

  // Handle saving editing changes
  const handleSaveEdits = React.useCallback(async () => {
    if (editingState.editMode && Object.keys(editingState.tables).length > 0) {
      const saveData = saveMetadata.convertEditingTableData(editingState.tables)
      const success = await saveMetadata.saveTableDescriptions(saveData)

      if (success) {
        // Refetch data and then clear edits
        tableQuery.refetch().finally(() => {
          editingHandlers.clearLocalEdits()
        })
      }
    }
  }, [editingState.editMode, editingState.tables, saveMetadata, editingHandlers, tableQuery])

  const { areAllItemsSelected, handleHeaderCheckboxChange, handleItemSelection } = tableSelection

  // For enriched data
  const columnDefs = React.useMemo(
    () =>
      createTableColumnDefinitions(
        enrichmentWorkflow.isEnrichmentInProcess,
        enrichmentState.showEnrichmentControls,
        enrichmentFields,
        handleNavigate,
        selectedTableIds,
        handleItemSelection,
        areAllItemsSelected,
        handleHeaderCheckboxChange,
        memoizedTables.length,
        enrichmentWorkflow.isEnrichmentCompleted,
        editingState.editingCell,
        editingState.editMode,
        editingState.editingField,
        editingHandlers.handleFieldEdit,
        editingHandlers.handleCellClick,
        editingHandlers.handleTextareaBlur,
        editingRefs.textareaRefs,
        editingState,
      ),
    [
      enrichmentWorkflow.isEnrichmentInProcess,
      enrichmentWorkflow.state.enrichmentState,
      selectedTableIds,
      areAllItemsSelected,
      handleItemSelection,
      handleHeaderCheckboxChange,
      memoizedTables.length,
      editingState.editingCell,
      editingState.editMode,
      editingState.editingField,
      editingState,
    ],
  )

  const enrichedTables = React.useMemo((): EnrichedTableType[] => {
    if (!enrichmentWorkflow.state.enrichmentData?.tables) {
      return memoizedTables.map((table) => ({ ...table, _isEnriched: false }))
    }

    const enrichedDataTables = enrichmentWorkflow.state.enrichmentData.tables
    const originalTables = memoizedTables

    // Merge enriched data with original data
    return originalTables.map((originalTable) => {
      const enrichedTable = enrichedDataTables.find(
        (et: any) => (et.tableName || et.table_name) === originalTable.table_name,
      )

      if (enrichedTable) {
        return {
          ...originalTable,
          table_description:
            enrichedTable.tableDescription ||
            enrichedTable.table_description ||
            originalTable.table_description,
          tags: enrichedTable.tags || originalTable.tags,
          table_domain:
            enrichedTable.tableDomain || enrichedTable.table_domain || originalTable.table_domain,
          _isEnriched: true,
        }
      }

      return {
        ...originalTable,
        _isEnriched: false,
      }
    })
  }, [enrichmentWorkflow.state.enrichmentData, memoizedTables])

  const originalTable = useReactTable<EnrichedTableType>({
    data: memoizedTables,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the table_name column
      const tableName = row.getValue('table_name') as string
      return tableName.toLowerCase().includes(value.toLowerCase())
    },
    state: {
      sorting,
      globalFilter,
    },
    enableColumnFilters: false,
  })

  const enrichedTable = useReactTable<EnrichedTableType>({
    data: enrichedTables,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    sortDescFirst: true,
    globalFilterFn: (row, columnId, value) => {
      // filter by the table_name column
      const tableName = row.getValue('table_name') as string
      return tableName.toLowerCase().includes(value.toLowerCase())
    },
    state: {
      sorting,
      globalFilter,
    },
    enableColumnFilters: false,
  })
  const shouldDisableClearAll = React.useMemo(
    () => shouldDisableClearAllButtonForTables(memoizedTables),
    [memoizedTables],
  )

  const showEnrichedDataView = enrichmentWorkflow.isEnrichmentCompleted
  const currentView = enrichmentWorkflow.state.currentView
  const activeTable =
    showEnrichedDataView && currentView === 'enriched' ? enrichedTable : originalTable
  const activeRows = activeTable.getRowModel().rows
  const hasNoTables = memoizedTables.length === 0

  const isInitialLoading = tableQuery.isPending
  const isTableLoading = isInitialLoading && hasNoTables

  return (
    <div className='flex w-full flex-col gap-6'>
      <PageTemplate.Header hasBreadcrumb className='flex-wrap items-center justify-between gap-4'>
        <div className='space-y-4'>
          <Breadcrumb items={breadcrumbItems} />
          <PageTemplate.H4>
            <T keyName='table.subtitle' />
          </PageTemplate.H4>
        </div>
        <div className='relative'>
          <SearchIcon className='absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-200' />
          <Input
            placeholder='Search'
            value={globalFilter}
            onChange={(e) => setGlobalFilter(e.target.value)}
            className='w-80 pl-10'
          />
        </div>
      </PageTemplate.Header>

      <div className='flex items-center gap-6'>
        <Button
          unstyled
          className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
          disabled={metadataScoreQuery.isLoading}
          onClick={() => {
            openDataGovernanceDashboardTab(
              'metaData',
              props.database_name,
              props.schema_name,
              'all',
              props.assetType,
              'all',
            )
          }}
        >
          <div className='flex flex-col items-start gap-1'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-normal'>Metadata Score</p>
              <TrendingUp className='size-4 text-neutral-200' />
            </div>
            {metadataScoreQuery.isLoading ? (
              <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
            ) : (
              <div className='flex items-center gap-2'>
                <span
                  className={`text-sm font-medium ${getStyles(metadataScoreQuery.data?.score || 0).text}`}
                >
                  {metadataScoreQuery.data?.score
                    ? `${Math.round(metadataScoreQuery.data.score)}%`
                    : 'N/A'}
                </span>
                <Progress
                  value={metadataScoreQuery.data?.score || 0}
                  className='h-1 w-16'
                  color={getStyles(metadataScoreQuery.data?.score || 0).progress}
                />
              </div>
            )}
          </div>
        </Button>
        <Button
          unstyled
          className='flex items-center gap-2 rounded-lg p-2 hover:bg-neutral-50 disabled:cursor-not-allowed'
          disabled={dataQualityScore.isLoading}
          onClick={() => {
            openDataGovernanceDashboardTab(
              'dataQuality',
              props.database_name,
              props.schema_name,
              'all',
              props.assetType,
              'all',
            )
          }}
        >
          <div className='flex flex-col items-start gap-1'>
            <div className='flex items-center gap-2'>
              <p className='text-sm font-normal'>Data Quality Score</p>
              <Target className='size-4 text-neutral-200' />
            </div>
            {dataQualityScore.isLoading ? (
              <div className='mt-2 h-2 w-24 animate-pulse rounded-full bg-gray-200' />
            ) : (
              <div className='flex items-center gap-2'>
                <span
                  className={`text-sm font-medium ${getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0).text}`}
                >
                  {dataQualityScore.data?.overall_assessment.percentage_score
                    ? `${Math.round(dataQualityScore.data?.overall_assessment.percentage_score)}%`
                    : 'N/A'}
                </span>
                <Progress
                  value={dataQualityScore.data?.overall_assessment.percentage_score || 0}
                  className='h-1 w-16'
                  color={
                    getStyles(dataQualityScore.data?.overall_assessment.percentage_score || 0)
                      .progress
                  }
                />
              </div>
            )}
          </div>
        </Button>
      </div>
      {metadataScoreQuery.isFetching || dataQualityScore.isFetching ? <ScoreCardsUpdate /> : <></>}

      <div className='flex justify-between'>
        <EnrichmentControls
          type='table'
          isNoData={isNoData}
          enrichmentState={enrichmentState}
          setEnrichmentState={setEnrichmentState}
          enrichmentWorkflow={enrichmentWorkflow}
          onEnrich={handlers.handleEnrich}
          onCancel={handlers.handleCancel}
          onSave={handlers.handleSave}
          onUndoEnrichment={handlers.handleUndoEnrichment}
          onRedoEnrichment={handlers.handleRedoEnrichment}
          onClearAllMetadata={handlers.handleClearAllMetadata}
          disableClearAllMetadata={shouldDisableClearAll || hasNoTables}
          isSaving={handlers.isSavingMetadata}
          isClearingMetadata={handlers.isClearingMetadata}
          hasLocalEdits={editingState.editMode}
          onClearLocalEdits={editingHandlers.clearLocalEdits}
          onSaveEdits={handleSaveEdits}
          isSavingEdits={saveMetadata.isSaving}
          latestUpdate={enrichmentWorkflow.latestUpdate}
        />
      </div>

      <div className='w-full'>
        <Table>
          <TableHeader>
            {activeTable.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead
                    key={header.id}
                    style={{ width: `${header.getSize()}px` }}
                    data-state={
                      (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                    }
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isTableLoading && <TableSkeletonRows rowCount={3} cellCount={columnDefs.length} />}

            {tableQuery.isError && <TableErrorRow colSpan={columnDefs.length} />}

            {tableQuery.isSuccess &&
              activeRows.length > 0 &&
              activeRows.map((row) => {
                const tableName = row.original.table_name
                const isSelected = selectedTableIds.includes(tableName)
                const isRowEdited = editingState.tables[tableName] !== undefined
                return (
                  <TableRow
                    key={row.id}
                    data-state={row.getIsSelected() && 'selected'}
                    className={`h-[72px] ${isSelected || isRowEdited ? 'bg-neutral-25' : ''}`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        style={{ width: `${cell.column.getSize()}px` }}
                        data-state={
                          (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                        }
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}

            {tableQuery.isSuccess && activeRows.length === 0 && !tableQuery.isFetching && (
              <TableRow>
                <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                  <T keyName='table.messages.noTables' />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}

export const createEditableTableColumn = (
  fieldName: EditableFieldName,
  editingCell: string | null,
  editingField: EditableFieldName | null,
  handleFieldEdit: (columnName: string, fieldName: EditableFieldName, value: string) => void,
  handleCellClick: (columnName: string, fieldName?: EditableFieldName) => void,
  handleTextareaBlur: () => void,
  textareaRefs: React.MutableRefObject<Record<string, HTMLTextAreaElement>>,
  editingState: any,
  isEnrichmentInProcess: boolean = false,
  showEnrichmentControls: boolean = false,
  isEnrichmentCompleted: boolean = false,
  enrichmentFields: string[] = [],
  selectedTableIds: string[] = [],
): ColumnDef<EnrichedTableType> => {
  return {
    size: 500,
    accessorKey: fieldName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='table.table.headers.description'>
        <T keyName='table.table.headers.description' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell, row }: CellContext<EnrichedTableType, unknown>) => {
      const value = cell.getValue<string>()
      const tableName = row.original.table_name
      const isEnrichingField = isEnrichmentInProcess && enrichmentFields.includes(fieldName)
      const shouldShowSkeleton =
        isEnrichingField && (selectedTableIds.length === 0 || selectedTableIds.includes(tableName))
      const isEditingDisabled =
        isEnrichmentInProcess || showEnrichmentControls || isEnrichmentCompleted

      if (shouldShowSkeleton) {
        return <Skeleton className='h-5' />
      }

      return (
        <EditableCell
          fieldName={fieldName}
          value={value}
          columnName={tableName}
          isEditing={editingCell === tableName && editingField === fieldName}
          isEditingDisabled={isEditingDisabled}
          onEdit={(columnName: string, value: string) =>
            handleFieldEdit(columnName, fieldName, value)
          }
          onCellClick={handleCellClick}
          onBlur={handleTextareaBlur}
          textareaRefs={textareaRefs}
          editingState={editingState}
        />
      )
    },
  }
}

const createTableColumnDefinitions = (
  isEnrichmentInProcess: boolean,
  showEnrichmentControls: boolean,
  enrichmentFields: string[],
  handleNavigate: (tableName: string) => void,
  selectedTableIds: string[],
  handleItemSelection: (tableName: string) => void,
  areAllItemsSelected: boolean,
  handleHeaderCheckboxChange: () => void,
  totalTablesCount: number,
  isEnrichmentCompleted: boolean = false,
  editingCell: string | null = null,
  editMode: boolean = false,
  editingField: EditableFieldName | null = null,
  handleFieldEdit: (
    columnName: string,
    fieldName: EditableFieldName,
    value: string,
  ) => void = () => {},
  handleCellClick: (columnName: string, fieldName?: EditableFieldName) => void = () => {},
  handleTextareaBlur: () => void = () => {},
  textareaRefs: React.MutableRefObject<Record<string, HTMLTextAreaElement>> = { current: {} },
  editingState: any = { tables: {}, columns: {} },
): ColumnDef<EnrichedTableType>[] => [
  // 1. Select column
  {
    id: 'select',
    size: 50,
    header: () => (
      <Checkbox
        variant='neutral'
        checked={areAllItemsSelected}
        showUnselectIcon={areAllItemsSelected}
        onCheckedChange={handleHeaderCheckboxChange}
        disabled={
          totalTablesCount === 0 || isEnrichmentCompleted || isEnrichmentInProcess || editMode
        }
        title={(() => {
          if (totalTablesCount === 0) {
            return 'No tables to select'
          }

          if (areAllItemsSelected) {
            return 'Unselect all'
          }

          return 'Select all'
        })()}
      />
    ),
    cell: ({ row }) => {
      const tableName = row.original.table_name
      const isSelected = selectedTableIds.includes(tableName)

      return (
        <Checkbox
          variant='neutral'
          checked={isSelected}
          onCheckedChange={() => handleItemSelection(tableName)}
          disabled={isEnrichmentCompleted || isEnrichmentInProcess || editMode}
          title={isSelected ? 'Unselect' : 'Select'}
        />
      )
    },
  },
  // 2. Table name column
  {
    size: 100,
    accessorKey: 'table_name',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='table.table.headers.tableName'>
        <T keyName='table.table.headers.tableName' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }: CellContext<EnrichedTableType, unknown>) => {
      const tableName = cell.getValue<string>()
      return (
        <NameButton
          name={tableName}
          onClick={() => handleNavigate(tableName)}
          title={`View table details for ${tableName}`}
        />
      )
    },
  },
  // 3. Description column (editable)
  createEditableTableColumn(
    'table_description',
    editingCell,
    editingField,
    handleFieldEdit,
    handleCellClick,
    handleTextareaBlur,
    textareaRefs,
    editingState,
    isEnrichmentInProcess,
    showEnrichmentControls,
    isEnrichmentCompleted,
    enrichmentFields,
    selectedTableIds,
  ),
  // 4. Tags column
  {
    accessorKey: 'tags',
    header: () => <T keyName='table.table.headers.tags' />,
    cell: ({ cell }: CellContext<EnrichedTableType, unknown>) => {
      const rawTags = cell.getValue<TagsStructure>() || { PII_DG: [], DATA_QUALITY_TAGS: [] }
      const piiTags = extractPiiTags(rawTags)
      const dataQualityTags = extractDataQualityTags(rawTags)
      const allTags = [...piiTags, ...dataQualityTags]

      return <TagsCell tags={allTags} category={TAG_CATEGORIES.PII_DG} isEditingDisabled={true} />
    },
  },
  // 5. Sensitivity column
  {
    accessorKey: 'sensitivity_tags',
    header: () => <span>Sensitivity</span>,
    cell: ({ cell, row }: CellContext<EnrichedTableType, unknown>) => {
      const sensitivityTags = extractSensitivityTags(row.original.tags)
      return <SensitivityTagsCell sensitivityTags={sensitivityTags} />
    },
  },
  // 6. Owners column
  {
    size: 150,
    accessorKey: 'table_owners',
    header: () => <T keyName='table.table.headers.owners' />,
    cell: ({ cell }: CellContext<EnrichedTableType, unknown>) => {
      const owners = cell.getValue<string[]>() || []

      if (owners.length === 0) {
        return <div className='text-sm'>-</div>
      }

      return <OwnersCell owners={owners} />
    },
  },
  // 7. Row count column
  {
    size: 50,
    accessorKey: 'row_count',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='table.table.headers.rowCount'>
        <T keyName='table.table.headers.rowCount' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }: CellContext<EnrichedTableType, unknown>) => {
      const value = cell.getValue<number | null | undefined>()
      return <p className='text-sm'>{value != null ? value.toLocaleString() : '-'}</p>
    },
  },
  // 8. Column count column
  {
    size: 50,
    accessorKey: 'column_count',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} titleKey='table.table.headers.columnCount'>
        <T keyName='table.table.headers.columnCount' />
      </DataTableColumnHeader>
    ),
    cell: ({ cell }: CellContext<EnrichedTableType, unknown>) => {
      const value = cell.getValue<number | null | undefined>()
      return <p className='text-sm'>{value ?? '-'}</p>
    },
  },
]

export { TableList }
